import { CustomStrategyConstants } from "utils/constants";
import { getDebtBasedWeeks } from "utils/functions";

export const calculateRevisedWeeks = ({
  initial_payment_timeline,
  commission_timeline,
  include_commission_payment,
  totalDebt,
  weekly_budget,
  weeklyCommissionPayment,
  weeklyInitialPayment,
  total_lawsuits,
}: {
  initial_payment_timeline: number;
  commission_timeline: number;
  include_commission_payment: boolean;
  totalDebt: number;
  weekly_budget: number;
  weeklyCommissionPayment: number;
  weeklyInitialPayment: number;
  total_lawsuits: number;
}): { weeks: number; creditor_estimate: number; creditor_estimate_split: { amount: number; weeks: number }[] } => {
  const multiplier = total_lawsuits < 2 ? 1 : total_lawsuits;
  if (initial_payment_timeline === 1) {
    if (include_commission_payment === false) {
      const creditor_estimate_split: { amount: number; weeks: number }[] = [];
      const creditor_estimate = weekly_budget - weeklyCommissionPayment - (multiplier * 36.25 + 25);
      let weeks = Math.floor(totalDebt / creditor_estimate);
      const remaining = totalDebt - creditor_estimate * weeks;

      creditor_estimate_split.push({ amount: creditor_estimate, weeks });
      if (remaining > 0) {
        creditor_estimate_split.push({ amount: remaining, weeks: Math.ceil(remaining / creditor_estimate) });
        weeks += Math.ceil(remaining / creditor_estimate);
      }

      return { creditor_estimate, weeks, creditor_estimate_split };
    } else {
      const creditor_estimate_split = [
        { amount: weekly_budget - weeklyCommissionPayment - (multiplier * 36.25 + 25), weeks: commission_timeline },
      ];
      const first_debt_clearance = creditor_estimate_split[0].amount * commission_timeline;
      const remaining_debt = totalDebt - first_debt_clearance;
      const creditor_estimate = weekly_budget - (multiplier * 36.25 + 25);
      const remaining_weeks = Math.floor(remaining_debt / creditor_estimate);
      let weeks = commission_timeline + remaining_weeks;

      const remaining =
        totalDebt -
        (creditor_estimate * remaining_weeks + creditor_estimate_split[0].amount * creditor_estimate_split[0].weeks);
      // console.log(remaining);

      creditor_estimate_split.push({ amount: creditor_estimate, weeks: remaining_weeks });
      if (remaining > 0) {
        creditor_estimate_split.push({ amount: remaining, weeks: Math.ceil(remaining / creditor_estimate) });
        weeks += Math.ceil(remaining / creditor_estimate);
      }

      return { creditor_estimate, creditor_estimate_split, weeks };
    }
  } else {
    const creditor_estimate_split: { amount: number; weeks: number }[] = [];
    creditor_estimate_split.push({
      amount: weekly_budget - weeklyCommissionPayment - weeklyInitialPayment - (multiplier * 36.25 + 25),
      weeks: initial_payment_timeline - 1,
    });
    const first_debt_clearance = creditor_estimate_split[0].amount * (initial_payment_timeline - 1);
    creditor_estimate_split.push({
      amount: weekly_budget - weeklyCommissionPayment - (multiplier * 36.25 + 25),
      weeks: commission_timeline - initial_payment_timeline + 1,
    });
    const next_debt_clearance =
      creditor_estimate_split[1].amount * (commission_timeline - initial_payment_timeline + 1);
    const remaining_debt = totalDebt - (first_debt_clearance + next_debt_clearance);

    if (include_commission_payment === false) {
      const creditor_estimate = weekly_budget - weeklyCommissionPayment - (multiplier * 36.25 + 25);
      const remaining_weeks = Math.floor(remaining_debt / creditor_estimate);
      let weeks = commission_timeline + remaining_weeks;

      const remaining =
        totalDebt -
        (creditor_estimate * remaining_weeks +
          creditor_estimate_split[0].amount * creditor_estimate_split[0].weeks +
          creditor_estimate_split[1].amount * creditor_estimate_split[1].weeks);
      // console.log(remaining);

      creditor_estimate_split.push({ amount: creditor_estimate, weeks: remaining_weeks });
      if (remaining > 0) {
        creditor_estimate_split.push({ amount: remaining, weeks: Math.ceil(remaining / creditor_estimate) });
        weeks += Math.ceil(remaining / creditor_estimate);
      }

      return { weeks, creditor_estimate, creditor_estimate_split };
    } else {
      const creditor_estimate = weekly_budget - (multiplier * 36.25 + 25);
      const remaining_weeks = Math.floor(remaining_debt / creditor_estimate);
      let weeks = commission_timeline + remaining_weeks;

      const remaining =
        totalDebt -
        (creditor_estimate * remaining_weeks +
          creditor_estimate_split[0].amount * creditor_estimate_split[0].weeks +
          creditor_estimate_split[1].amount * creditor_estimate_split[1].weeks);
      // console.log(remaining);

      creditor_estimate_split.push({ amount: creditor_estimate, weeks: remaining_weeks });
      if (remaining > 0) {
        creditor_estimate_split.push({ amount: remaining, weeks: Math.ceil(remaining / creditor_estimate) });
        weeks += Math.ceil(remaining / creditor_estimate);
      }

      return { weeks, creditor_estimate, creditor_estimate_split };
    }
  }
};

export const calculateNetCommissionAmount = (
  net_commission: number,
  totalDebt: number,
  initial_payment_charge: number,
) => {
  return ((net_commission - initial_payment_charge) / 100) * totalDebt;
};

export const calculateWeeklyInitialPayment = (initial_payment_timeline: number, initial_payment: number) => {
  return initial_payment_timeline ? Math.floor(initial_payment / initial_payment_timeline) : 0;
};

export const calculateWeeklyCommissionAmount = (
  commission_timeline: number,
  net_commission: number,
  totalDebt: number,
  initial_payment_charge: number,
  weekly_budget: number,
  bias_amount: number,
) => {
  return commission_timeline > 0 &&
    (calculateNetCommissionAmount(net_commission, totalDebt, initial_payment_charge) - bias_amount) /
      commission_timeline >
      weekly_budget
    ? -1
    : Math.floor(
        (calculateNetCommissionAmount(net_commission, totalDebt, initial_payment_charge) - bias_amount) /
          commission_timeline,
      );
};

export const getMinimumWeeklyBudget = (
  initial_payment_timeline: number,
  initial_payment: number,
  // weekly_budget: number,
  // weeklyCommissionPayment: number,
  total_lawsuits: number,
  commision: {
    net_commission: number;
    totalDebt: number;
    initial_payment_charge: number;
    commission_timeline: number;
  },
) => {
  const { net_commission, totalDebt, initial_payment_charge, commission_timeline } = commision;
  const _weeklyCommision =
    calculateNetCommissionAmount(net_commission, totalDebt, initial_payment_charge) / commission_timeline;
  // if (_weeklyCommision < 0) return weekly_budget;
  // 21*45900/100/40+150+25+36.25*1+1259/3

  const multiplier = total_lawsuits < 2 ? 1 : total_lawsuits;
  const splitAmount = initial_payment_timeline > 1 ? initial_payment / initial_payment_timeline : 0;

  return _weeklyCommision + 100 + (multiplier * 36.25 + 25) + splitAmount;
};

export const getTotalInitialPayment = (
  initial_payment_charge: number,
  totalDebt: number,
  extraAmount: number,
  bias_amount: number,
) => {
  return Number(((initial_payment_charge / 100) * totalDebt + extraAmount + bias_amount).toFixed(2));
};

export const getCalculationFields = (
  totalDebt: number,
  extra: number,
  weekly_budget: number,
  total_lawsuits: number,
  bias_amount: number,
) => {
  const initial_payment: number = getTotalInitialPayment(
    CustomStrategyConstants.initial_payment_charge,
    totalDebt,
    extra,
    bias_amount,
  );
  const initial_payment_timeline: number = 1;
  const net_commission: number = CustomStrategyConstants.total_interest * 100;
  const commission_timeline: number = getDebtBasedWeeks(totalDebt).interest_weeks_3;
  const include_commission_payment: boolean = false;
  const initial_payment_charge: number = CustomStrategyConstants.initial_payment_charge;
  const multiplier = total_lawsuits < 2 ? 1 : total_lawsuits;

  const weeklyCommissionPayment = calculateWeeklyCommissionAmount(
    commission_timeline,
    net_commission,
    totalDebt,
    initial_payment_charge,
    weekly_budget,
    bias_amount,
  );

  const weeklyInitialPayment = calculateWeeklyInitialPayment(initial_payment_timeline, initial_payment);
  const { weeks, creditor_estimate, creditor_estimate_split } = calculateRevisedWeeks({
    initial_payment_timeline,
    commission_timeline,
    include_commission_payment,
    totalDebt,
    weekly_budget,
    weeklyCommissionPayment,
    weeklyInitialPayment,
    total_lawsuits,
  });
  // console.log({
  //   initial_payment_timeline,
  //   commission_timeline,
  //   include_commission_payment,
  //   totalDebt,
  //   weekly_budget,
  //   weeklyCommissionPayment,
  //   weeklyInitialPayment,
  //   total_lawsuits,
  // });

  return {
    include_commission_payment,
    initial_payment,
    initial_payment_timeline,
    net_commission,
    commission_timeline,
    initial_payment_charge,
    weeklyCommissionPayment,
    weeklyInitialPayment,
    weeks,
    creditor_estimate,
    creditor_estimate_split,
  };
};
